import {
    DayTripDetailModel,
    DayTripStop,
    getSeatingMaps,
    SEATING_CAPACITIES,
    SeatingTypeItem,
} from "./dayTripDetailTypes"

/** Parse composite id "excursion:id" or "place:id" into { type, id } */
function parseCompositeId(compositeId: string): { type: "excursion" | "place"; id: string } | null {
    if (!compositeId?.trim()) return null
    if (compositeId.startsWith("place:"))
        return { type: "place", id: compositeId.slice(6) }
    if (compositeId.startsWith("excursion:"))
        return { type: "excursion", id: compositeId.slice(10) }
    return null
}

export interface DayTripPutStop {
    day_trip_stop_id: string | null
    stop_point_excrusion_id: string | null
    stop_point_excrusion_name: string | null
    stop_point_place_id: string | null
    stop_point_place_name: string | null
    flag_active: boolean
}

export interface DayTripPutPricing {
    trip_pricing_id: string | null
    seating_type_id: number
    // SIC is sent here as a per-person row (flag_per_person:true); seater rows are per-vehicle.
    flag_per_person: boolean
    price: number
    flag_active: boolean
}

export interface DayTripPutDinnerPricing {
    // null when adding a new row. The SIC row is identified by its seating_type_id.
    trip_pricing_id: string | null
    seating_type_id: number
    price: number
    flag_active: boolean
}

export interface DayTripPutBody {
    day_trip_id: string | null
    day_trip_name: string
    day_trip_type_id: number | null
    day_trip_type_name: string | null
    city_id: number | null
    city_name: string | null
    starting_point_excursion_id: string | null
    starting_point_place_id: string | null
    end_point_excursion_id: string | null
    end_point_place_id: string | null
    flag_enable_sic: boolean
    flag_enable_dinner: boolean
    flag_active: boolean
    day_trip_itinerary: string | null
    day_trip_stop: DayTripPutStop[]
    day_trip_pricing: DayTripPutPricing[]
    dinner_pricing: DayTripPutDinnerPricing[]
}

function getMaps(seating_type?: SeatingTypeItem[] | null) {
    return seating_type?.length ? getSeatingMaps(seating_type) : null
}

/** SIC is a named entry in the generic seating_type list (no numeric capacity, e.g. "SIC"). */
function findSicSeatingType(
    seating_type?: SeatingTypeItem[] | null
): { id: number; name: string } | null {
    const match = (seating_type ?? []).find(
        (st) => String(st.seating_type_name || "").trim().toUpperCase() === "SIC"
    )
    return match ? { id: match.seating_type_id, name: match.seating_type_name } : null
}

const FALLBACK_CAPACITY_TO_ID: Record<number, number> = {
    4: 1, 7: 2, 16: 3, 29: 4, 35: 5, 45: 6,
}
const FALLBACK_ID_TO_CAPACITY: Record<number, number> = {
    1: 4, 2: 7, 3: 16, 4: 29, 5: 35, 6: 45,
}

export function modelToPutBody(
    model: DayTripDetailModel,
    seating_type?: SeatingTypeItem[] | null
): DayTripPutBody {
    const start = parseCompositeId(model.start_point_id)
    const end = parseCompositeId(model.end_point_id)
    const maps = getMaps(seating_type)
    const capacityToId = maps?.capacityToSeatingTypeId ?? FALLBACK_CAPACITY_TO_ID

    const day_trip_stop: DayTripPutStop[] = (model.stops ?? []).map((s) => {
        const stopPoint = parseCompositeId(s.stop_id)
        const isExcursion = stopPoint?.type === "excursion"
        return {
            day_trip_stop_id: s.day_trip_stop_id ?? null,
            stop_point_excrusion_id: isExcursion ? stopPoint!.id : null,
            stop_point_excrusion_name: isExcursion ? s.stop_name : null,
            stop_point_place_id: stopPoint?.type === "place" ? stopPoint.id : null,
            stop_point_place_name: stopPoint?.type === "place" ? s.stop_name : null,
            flag_active: true,
        }
    })

    const sicType = findSicSeatingType(seating_type)

    const day_trip_pricing: DayTripPutPricing[] = (model.seating_prices ?? [])
        .filter((p) => p.capacity != null && capacityToId[p.capacity] != null)
        .map((p) => ({
            trip_pricing_id: p.trip_pricing_id ?? null,
            seating_type_id: capacityToId[p.capacity],
            flag_per_person: false,
            price: p.price_per_vehicle ?? 0,
            flag_active: true,
        }))
    // SIC price now travels inside day_trip_pricing as a per-person row (no longer top-level).
    if (model.flag_enable_sic && sicType) {
        day_trip_pricing.push({
            trip_pricing_id: null,
            seating_type_id: sicType.id,
            flag_per_person: true,
            price: model.sic_price_per_person ?? 0,
            flag_active: true,
        })
    }

    // Dinner-transfer pricing: per-vehicle rows per seater + (when SIC is also on) the SIC row
    // (identified by its seating_type_id, no per-person flag in the payload).
    const dinner_pricing: DayTripPutDinnerPricing[] = []
    if (model.flag_enable_dinner) {
        for (const p of model.dinner_transfer_prices ?? []) {
            if (p.capacity == null || capacityToId[p.capacity] == null) continue
            dinner_pricing.push({
                trip_pricing_id: p.trip_pricing_id ?? null,
                seating_type_id: capacityToId[p.capacity],
                price: p.price_per_vehicle ?? 0,
                flag_active: true,
            })
        }
        if (model.flag_enable_sic && sicType) {
            dinner_pricing.push({
                trip_pricing_id: null,
                seating_type_id: sicType.id,
                price: model.dinner_transfer_sic_price_per_person ?? 0,
                flag_active: true,
            })
        }
    }

    return {
        day_trip_id: model.day_trip_id || null,
        day_trip_name: model.trip_name ?? "",
        day_trip_type_id: model.day_trip_type_id ?? null,
        day_trip_type_name: model.day_trip_type_name?.trim() ? model.day_trip_type_name : null,
        city_id: model.city_id ?? null,
        city_name: model.city_name?.trim() ? model.city_name : null,
        starting_point_excursion_id: start?.type === "excursion" ? start.id : null,
        starting_point_place_id: start?.type === "place" ? start.id : null,
        end_point_excursion_id: end?.type === "excursion" ? end.id : null,
        end_point_place_id: end?.type === "place" ? end.id : null,
        flag_enable_sic: model.flag_enable_sic ?? true,
        flag_enable_dinner: model.flag_enable_dinner ?? false,
        flag_active: model.flag_active ?? false,
        // Empty/whitespace-only itinerary HTML is normalized to null.
        day_trip_itinerary: model.day_trip_itinerary?.trim() ? model.day_trip_itinerary : null,
        day_trip_stop,
        day_trip_pricing,
        dinner_pricing,
    }
}

export interface DayTripDetailApiStop {
    day_trip_stop_id?: string | null
    stop_point_excrusion_id?: string | null
    stop_point_excrusion_name?: string | null
    stop_point_place_id?: string | null
    stop_point_place_name?: string | null
    flag_active?: boolean
}

export interface DayTripDetailApiPricing {
    trip_pricing_id?: string | null
    seating_type_id?: number
    seating_type_name?: string
    flag_per_person?: boolean
    price?: number
    flag_active?: boolean
}

export interface DayTripDetailApiDinnerPricing {
    trip_pricing_id?: string | null
    trip_service_type_id?: number | null
    trip_service_type_name?: string | null
    day_trip_id?: string
    day_trip_name?: string
    seating_type_id?: number
    seating_type_name?: string
    flag_per_person?: boolean
    price?: number
    flag_active?: boolean
}

export interface DayTripDetailApiRow {
    day_trip_id?: string
    day_trip_name?: string
    day_trip_type_id?: number | null
    day_trip_type_name?: string | null
    city_id?: number | null
    city_name?: string | null
    day_trip_itinerary?: string | null
    starting_point_excursion_id?: string | null
    starting_point_excursion_name?: string | null
    starting_point_place_id?: string | null
    starting_point_place_name?: string | null
    end_point_excursion_id?: string | null
    end_point_excursion_name?: string | null
    end_point_place_id?: string | null
    end_point_place_name?: string | null
    flag_enable_sic?: boolean
    flag_enable_dinner?: boolean
    sic_price_per_person?: number
    flag_active?: boolean
    day_trip_stop?: DayTripDetailApiStop[]
    day_trip_pricing?: DayTripDetailApiPricing[]
    dinner_pricing?: DayTripDetailApiDinnerPricing[]
}

function toCompositeFromApi(
    excursionId: string | null | undefined,
    placeId: string | null | undefined,
    excursionName: string | null | undefined,
    placeName: string | null | undefined
): { id: string; name: string } {
    if (placeId) {
        return {
            id: `place:${placeId}`,
            name: placeName ?? "",
        }
    }
    if (excursionId) {
        return {
            id: `excursion:${excursionId}`,
            name: excursionName ?? "",
        }
    }
    return { id: "", name: "" }
}

export function apiRowToModel(
    row: DayTripDetailApiRow,
    seating_type?: SeatingTypeItem[] | null
): DayTripDetailModel {
    const start = toCompositeFromApi(
        row.starting_point_excursion_id,
        row.starting_point_place_id,
        row.starting_point_excursion_name,
        row.starting_point_place_name
    )
    const end = toCompositeFromApi(
        row.end_point_excursion_id,
        row.end_point_place_id,
        row.end_point_excursion_name,
        row.end_point_place_name
    )

    const stops: DayTripStop[] = (row.day_trip_stop ?? []).map((s) => {
        const stop = toCompositeFromApi(
            s.stop_point_excrusion_id,
            s.stop_point_place_id,
            s.stop_point_excrusion_name,
            s.stop_point_place_name
        )
        return {
            stop_id: stop.id,
            stop_name: stop.name,
            day_trip_stop_id: s.day_trip_stop_id ?? undefined,
        }
    })

    const idToCapacity = seating_type?.length
        ? getSeatingMaps(seating_type).seatingTypeIdToCapacity
        : FALLBACK_ID_TO_CAPACITY
    const capacities = seating_type?.length
        ? getSeatingMaps(seating_type).capacities
        : [...SEATING_CAPACITIES]
    const sicId = findSicSeatingType(seating_type)?.id ?? null
    // SIC rows are identified by the SIC seating_type_id (with flag_per_person as a fallback for
    // payloads that still send it).
    const isSicRow = (p: { seating_type_id?: number; flag_per_person?: boolean }) =>
        Boolean(p.flag_per_person) || (sicId != null && p.seating_type_id === sicId)

    const priceByCapacity: Record<number, { price: number; trip_pricing_id?: string | null }> = {}
    for (const p of row.day_trip_pricing ?? []) {
        if (isSicRow(p)) continue // SIC row handled below, not a seater
        if (p.seating_type_id != null) {
            const cap = idToCapacity[p.seating_type_id]
            if (cap != null) {
                priceByCapacity[cap] = {
                    price: p.price ?? 0,
                    trip_pricing_id: p.trip_pricing_id ?? undefined,
                }
            }
        }
    }
    const seating_prices = capacities.map((capacity) => {
        const entry = priceByCapacity[capacity]
        return {
            capacity,
            price_per_vehicle: entry?.price ?? 0,
            trip_pricing_id: entry?.trip_pricing_id,
        }
    })

    // SIC price arrives as the SIC row inside day_trip_pricing (fallback to legacy top-level field).
    const mainSicRow = (row.day_trip_pricing ?? []).find((p) => isSicRow(p))
    const sicPricePerPerson = mainSicRow?.price ?? row.sic_price_per_person ?? 0

    // Dinner-transfer pricing: per-vehicle seater rows + the SIC row.
    const dinnerByCapacity: Record<number, { price: number; trip_pricing_id?: string | null }> = {}
    let dinnerSicPrice = 0
    for (const p of row.dinner_pricing ?? []) {
        if (isSicRow(p)) {
            dinnerSicPrice = p.price ?? 0
            continue
        }
        if (p.seating_type_id != null) {
            const cap = idToCapacity[p.seating_type_id]
            if (cap != null) {
                dinnerByCapacity[cap] = {
                    price: p.price ?? 0,
                    trip_pricing_id: p.trip_pricing_id ?? undefined,
                }
            }
        }
    }
    const dinner_transfer_prices = capacities.map((capacity) => {
        const entry = dinnerByCapacity[capacity]
        return {
            capacity,
            price_per_vehicle: entry?.price ?? 0,
            trip_pricing_id: entry?.trip_pricing_id,
        }
    })

    return {
        day_trip_id: row.day_trip_id ?? "",
        trip_name: row.day_trip_name ?? "",
        day_trip_type_id: row.day_trip_type_id ?? null,
        day_trip_type_name: row.day_trip_type_name ?? "",
        city_id: row.city_id ?? null,
        city_name: row.city_name ?? "",
        day_trip_itinerary: row.day_trip_itinerary ?? "",
        start_point_id: start.id,
        start_point_name: start.name,
        end_point_id: end.id,
        end_point_name: end.name,
        stops,
        flag_active: row.flag_active ?? false,
        seating_prices,
        flag_enable_sic: row.flag_enable_sic ?? true,
        sic_price_per_person: sicPricePerPerson,
        flag_enable_dinner: row.flag_enable_dinner ?? false,
        dinner_transfer_prices,
        dinner_transfer_sic_price_per_person: dinnerSicPrice,
    }
}
